import { css } from '@emotion/react';

import hexToRgba from '../../utils/hexToRgba';

const screenSizes = {
  xxs: '269px',
  xs: '350px',
  s: '576px',
  m: '769px',
  l: '992px',
  lg: '1024px',
  xl: '1196px',
  xxl: '1600px',
};

// mm : min and max xs_s means from xs to s screen size
// so mm_xs_s will cover the media query starts at 350px and ends to 576px

const breakpoints = {
  mm_xxs_xs: `@media (min-width: ${screenSizes.xxs}) and (max-width: ${screenSizes.xs})`,
  mm_xs_s: `@media (min-width: ${screenSizes.xs}) and (max-width: ${screenSizes.s})`,
  mm_s_m: `@media (min-width: ${screenSizes.s}) and (max-width: ${screenSizes.m})`,
  mm_m_l: `@media (min-width: ${screenSizes.m}) and (max-width: ${screenSizes.l})`,
  mm_l_xl: `@media (min-width: ${screenSizes.l}) and (max-width: ${screenSizes.xl})`,
  mm_xl_xxl: `@media (min-width: ${screenSizes.xl}) and (max-width: ${screenSizes.xxl})`,
  mm_xxs_m: `@media (min-width: ${screenSizes.xxs}) and (max-width: ${screenSizes.m})`,

  xxs: `@media screen and (min-width: ${screenSizes.xxs})`,
  xs: `@media screen and (min-width: ${screenSizes.xs})`,
  s: `@media screen and (min-width: ${screenSizes.s})`,
  m: `@media screen and (min-width: ${screenSizes.m})`,
  l: `@media screen and (min-width: ${screenSizes.l})`,
  lg: `@media screen and (min-width: ${screenSizes.lg})`,
  xl: `@media screen and (min-width: ${screenSizes.xl})`,
  xxl: `@media screen and (min-width: ${screenSizes.xxl})`,
};

const fluidTypeScale = {
  font54_84: 'clamp(3.3313rem, 2.6602rem + 3.3554vi, 5.2606rem)',
  font18_20: 'clamp(1.125rem, 1.0815rem + 0.2174vi, 1.25rem)',
  font21_20: 'clamp(1.25rem, 1.3848rem + -0.1739vi, 1.35rem)',
  font20_18: 'clamp(1.125rem, 1.0815rem + 0.2174vi, 1.25rem)',
  font31_35: 'clamp(1.9438rem, 1.7657rem + 0.6627vi, 2.2213rem)',
  font15_15: 'clamp(0.9375rem, 0.9375rem + 0vi, 0.9375rem)',
  font12_11: 'clamp(0.7038rem, 0.8082rem + -0.1348vi, 0.7813rem)',
  font11_12: 'clamp(0.6875rem, 0.8139rem + -0.163vi, 0.7813rem)',
  font15_16: 'clamp(0.9375rem, 0.9158rem + 0.1087vi, 1rem);',
  font18_18: 'clamp(1.125rem, 1.125rem + 0vi, 1.125rem)',
  font12_12: 'clamp(0.7813rem, 0.7813rem + 0vi, 0.7813rem)',
  font32_32: 'clamp(2rem, 2rem + 0vi, 2rem)',
  font16_16: 'clamp(1rem, 1rem + 0vi, 1rem)',
  font20_20: 'clamp(1.25rem, 1.25rem + 0vw, 1.25rem);',
  font25_9_and_35_54: 'clamp(1.6188rem, 1.4092rem + 1.0478vw, 2.2213rem);',
};

const mediaQueries = {
  zoomedIn: `@media (min-aspect-ratio: 488/535) and (max-width: 767px)`, // 175% zoom level
  zoomedInL2: `@media (min-aspect-ratio: 488/535) and (max-width: 500px)`, // 300% zoom level
  zoomedInL3: `@media (min-aspect-ratio: 488/535) and (max-width: 420px)`, //400% zoom level
  iphoneZoom: `@media (min-aspect-ratio: 16/9) and (min-width: 375px) 
  and (max-width: 812px) 
  and (-webkit-min-device-pixel-ratio: 3)`, //for iphone-testing
  safariBrowser: `@media not all and (min-resolution:.001dpcm)`,
};

const colors = {
  white: '#FFFFFF',
  black: '#1D1D1B',
  blue: '#0068B2',
  darkBlue: '#004d83',
  lightBlue: '#B7C5E9',
  dodgerBlue: '#004F9F',
  surfaceBlue: '#00539F',
  red: '#E1261C',
  lightRed: '#ce1124',
  navy: '#011E41',
  checkNavy: '#1e438e',
  grey: '#c4c4c4',
  greyShade1: '#BBC1CE',
  greyShade2: '#f8f8f8',
  lightGrey: '#ECEEF3',
  darkGrey: '#5B6885',
  checkGrey: '#aab0c0',
  darkRed: '#A01E21',
  boldRed: '#c30024',
  buttonRed: '#DD221A',
  buttonBlue: '#024597',
  border_blue: '#AEBDE6',
  greyShade_eceef3: '#eceef3',
  color_aebde6: '#aebde6',
  red_FF0D00: '#FF0D00',
  blue_214C99: '#214C99',
  red_CB0A00: '#CB0A00',
  border_grey: '#E9EBF1',
  next_grey: '#515D7A',
  placeholder_black: '#041C39',
  placeholder_black2: '#1B1B1A',
  red_E02926: '#E02926',
  gender_grey: '#4F6074',
  bright_grey: '#E4E7EC',
  pale_blue: '#F0F1F4',
  pale_red: '#FBE1DF',
  alice_blue: '#F9FAFB',
  dark_black: '#000000',
  ocean_depth_navy: '#011228',
  divider_gray: '#424A5C',
  skip_yellow: '#FFDD00',
  border_black: '#1B1C1E',
  grey_dusty: '#626E88',
};

const spacing = {
  xs: '0.5rem',
  s: '1rem',
  m: '2rem',
  l: '3rem',
  xl: '4rem',
  xxl: '5rem',
};

const borderRadius = {
  xs: '0px',
  s: '2px',
  m: '4px',
  l: '8px',
  xl: '12px',
  xxl: '24px',
};

const heights = {
  mainNav: {
    mobile: '70px',
    desktop: '109px',
  },
  filterNav: '55px',
};

const headingStyle = {
  fontFamily: 'FS Dillon Bold',
  fontWeight: 700,
  fontStyle: 'italic',
  letterSpacing: '-0.02em',
};

const typography = {
  body: {
    desktop: {
      fontFamily: 'FS Dillon',
      fontSize: '1rem',
      lineHeight: '1.375rem',
      fontWeight: 400,
    },
    mobile: {
      fontFamily: 'FS Dillon',
      fontSize: '1rem',
      lineHeight: '1.375rem',
      fontWeight: 400,
    },
  },
  headings: {
    1: {
      desktop: {
        fontSize: '8.25rem',
        lineHeight: '6.25rem',
        ...headingStyle,
      },
      mobile: {
        fontSize: '8.25rem',
        lineHeight: '6.25rem',
        ...headingStyle,
      },
    },
    2: {
      desktop: {
        fontSize: '10.5rem',
        lineHeight: '7.5rem',
        ...headingStyle,
      },
      mobile: {
        fontSize: '7rem',
        lineHeight: '6rem',
        ...headingStyle,
      },
    },
    3: {
      desktop: {
        fontSize: '4.875rem',
        lineHeight: '4.25rem',
        ...headingStyle,
      },
      mobile: {
        fontSize: '4rem',
        lineHeight: '3rem',
        ...headingStyle,
      },
    },
    4: {
      desktop: {
        fontSize: '3rem',
        lineHeight: '2.625rem',
        ...headingStyle,
      },
      mobile: {
        fontSize: '2.4rem',
        lineHeight: '2.5rem',
        ...headingStyle,
      },
    },
    5: {
      desktop: {
        fontSize: '2.5rem',
        lineHeight: '2rem',
        ...headingStyle,
      },
      mobile: {
        fontSize: '2rem',
        lineHeight: '1.5rem',
        ...headingStyle,
      },
    },
    6: {
      desktop: {
        fontSize: '1.25rem',
        lineHeight: '1.375rem',
        ...headingStyle,
        fontFamily: 'FS Dillon',
        fontStyle: 'normal',
      },
      mobile: {
        fontSize: '1.25rem',
        lineHeight: '1.375rem',
        ...headingStyle,
        fontFamily: 'FS Dillon',
        fontStyle: 'normal',
      },
    },
    7: {
      desktop: {
        fontSize: '1.5rem',
        lineHeight: '1.625rem',
        ...headingStyle,
        fontFamily: 'FS Dillon',
        fontStyle: 'normal',
      },
      mobile: {
        fontSize: '1.25rem',
        lineHeight: '1.375rem',
        ...headingStyle,
        fontFamily: 'FS Dillon',
        fontStyle: 'normal',
      },
    },
  },
};

const utility = {
  buttonDefault: css`
    border: none;
    background: transparent;
    padding: 0;
    font: inherit;
    font-size: inherit;
    cursor: pointer;
    display: inherit;
    letter-spacing: inherit;
    line-height: inherit;
  `,
  checkboxHidden: css`
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    top: 0;
    width: 1px;
  `,
  overlay: css`
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: fixed;
    overflow: hidden;
    z-index: 11;
    background-color: ${hexToRgba(colors.darkGrey, 0.6)};
  `,
  outline: css`
    outline: 1px solid ${colors.red};
  `,
};

export const theme = {
  screenSizes,
  breakpoints,
  borderRadius,
  heights,
  colors,
  mediaQueries,
  spacing,
  typography,
  utility,
  fluidTypeScale,
};
